import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  MapPinIcon, 
  CurrencyRupeeIcon, 
  StarIcon,
  AdjustmentsHorizontalIcon,
  CheckIcon,
  ChevronUpDownIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { ExploreFilters, Skill } from '../../types';
import { SKILL_CATEGORIES } from '../../data/skillCategories';
import SkillSelector from '../common/SkillSelector';
import Button from '../common/Button';
import LocationPicker from '../common/LocationPicker';
import Switch from '../common/Switch';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: ExploreFilters;
  onApplyFilters: (filters: ExploreFilters) => void;
  onClearFilters: () => void;
  specializationOptions?: Skill[];
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onApplyFilters,
  onClearFilters,
  specializationOptions = []
}) => {
  const [localFilters, setLocalFilters] = useState<ExploreFilters>(filters);
  const [query, setQuery] = useState('');

  // Deduplicate options by ID first, then by name to avoid showing same option multiple times
  // and to prevent React key collisions
  const uniqueSpecializationOptions = useMemo(() => {
    // First, ensure unique IDs
    const uniqueIds = new Map<number, Skill>();
    specializationOptions.forEach(option => {
      if (!uniqueIds.has(option.id)) {
        uniqueIds.set(option.id, option);
      }
    });

    // Then, ensure unique Names from the unique ID set
    const seenNames = new Set<string>();
    const result: Skill[] = [];
    
    uniqueIds.forEach(option => {
      if (!seenNames.has(option.name)) {
        seenNames.add(option.name);
        result.push(option);
      }
    });
    
    // console.log('FilterModal: Specialization Options Debug', {
      // rawCount: specializationOptions.length,
      // uniqueIdCount: uniqueIds.size,
      // finalUniqueCount: result.length,
      // sample: result.slice(0, 3)
    // });
    
    return result;
  }, [specializationOptions]);

  const filteredSpecializations =
    query === ''
      ? uniqueSpecializationOptions
      : uniqueSpecializationOptions.filter((skill) =>
          skill.name
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  useEffect(() => {
    // Normalize specialization IDs to numbers to prevent string vs number comparison bugs
    const normalizedFilters = {
      ...filters,
      specializations: filters.specializations?.map(id => Number(id)) || []
    };
    setLocalFilters(normalizedFilters);
    if (isOpen) setQuery('');
  }, [filters, isOpen]);

  const handleChange = (key: keyof ExploreFilters, value: any) => {
    if (key === 'specializations' && Array.isArray(value)) {
      value = value.map(id => Number(id));
    }
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    const filtersToApply = {
      ...localFilters,
      specializations: localFilters.specializations?.map(id => Number(id)) || []
    };
    onApplyFilters(filtersToApply);
    onClose();
  };

  const handleClear = () => {
    onClearFilters();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center sm:items-center p-0 sm:p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
          onClick={onClose}
        />

        {/* Modal Panel */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 500 }}
          className="relative w-full max-w-lg transform bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl transition-all flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <AdjustmentsHorizontalIcon className="w-5 h-5" />
              Filters
            </h2>
            <button 
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto space-y-8">
            
            {/* Specializations */}
            <section>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <AdjustmentsHorizontalIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                Specializations
              </h3>
              
              <SkillSelector
                selectedSkillIds={localFilters.specializations || []}
                onChange={(newIds) => handleChange('specializations', newIds)}
                options={uniqueSpecializationOptions}
                placeholder="Search specializations..."
                categories={SKILL_CATEGORIES}
              />

            </section>

            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Location & Radius */}
            <section>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <MapPinIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                Location & Distance
              </h3>
              <div className="space-y-4">
                <LocationPicker
                  value={localFilters.location || ''}
                  onChange={(location, coords) => {
                    handleChange('location', location);
                    if (coords) {
                      handleChange('latitude', coords.lat);
                      handleChange('longitude', coords.lng);
                    }
                  }}
                  placeholder="Enter city or zip code"
                  className="w-full"
                />
                
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600 dark:text-gray-400">Search Radius</span>
                    <span className="font-medium text-gray-900 dark:text-white">{localFilters.radius || 50} km</span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="500"
                    step="5"
                    value={localFilters.radius || 50}
                    onChange={(e) => handleChange('radius', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
                  />
                  <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mt-1">
                    <span>5km</span>
                    <span>500km</span>
                  </div>
                </div>
              </div>
            </section>

            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Budget / Hourly Rate */}
            <section>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CurrencyRupeeIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                Hourly Rate
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Min Rate</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                    <input
                      type="number"
                      value={localFilters.hourly_rate_min || ''}
                      onChange={(e) => handleChange('hourly_rate_min', e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="0"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Max Rate</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                    <input
                      type="number"
                      value={localFilters.hourly_rate_max || ''}
                      onChange={(e) => handleChange('hourly_rate_max', e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="Any"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </section>

            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Rating */}
            <section>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <StarIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                Minimum Rating
              </h3>
              <div className="flex gap-2">
                {[4, 3, 2, 1].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => handleChange('rating', localFilters.rating === rating ? undefined : rating)}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                      localFilters.rating === rating
                        ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-500 text-teal-700 dark:text-teal-400 ring-1 ring-teal-500'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span>{rating}+</span>
                    <StarIconSolid className={`w-3 h-3 ${localFilters.rating === rating ? 'text-teal-500' : 'text-yellow-400'}`} />
                  </button>
                ))}
              </div>
            </section>

            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Experience Level */}
            <section>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Experience Level</h3>
              <div className="flex flex-wrap gap-2">
                {['beginner', 'intermediate', 'professional'].map((level) => (
                  <button
                    key={level}
                    onClick={() => handleChange('experience', localFilters.experience === level ? undefined : level)}
                    className={`px-4 py-2 rounded-full text-sm border transition-all capitalize ${
                      localFilters.experience === level
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </section>

            {localFilters.user_type === 'model' && (
              <>
                <hr className="border-gray-100 dark:border-gray-800" />
                
                {/* Model Specific Filters */}
                <section className="space-y-6">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Model Specifications</h3>
                  
                  {/* Gender Selection */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-505 dark:text-gray-400 mb-2">Gender</label>
                    <div className="flex gap-2">
                      {['female', 'male', 'non-binary'].map((g) => {
                        const isSel = localFilters.gender === g;
                        return (
                          <button
                            type="button"
                            key={g}
                            onClick={() => handleChange('gender', isSel ? undefined : g)}
                            className={`flex-1 py-2 px-3 rounded-xl border text-xs font-semibold capitalize transition-all ${
                              isSel
                                ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-500 text-teal-700 dark:text-teal-400 font-bold'
                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {g}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Height Slider */}
                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-2">
                      <span className="text-gray-500 dark:text-gray-400">Height Range</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {localFilters.height_min || 140}cm - {localFilters.height_max || 210}cm
                      </span>
                    </div>
                    <div className="flex gap-4 items-center">
                      <input
                        type="range"
                        min="140"
                        max="220"
                        value={localFilters.height_min || 140}
                        onChange={(e) => handleChange('height_min', parseInt(e.target.value))}
                        className="w-full h-1.5 bg-gray-250 rounded-lg appearance-none cursor-pointer accent-teal-600"
                      />
                      <input
                        type="range"
                        min="140"
                        max="220"
                        value={localFilters.height_max || 210}
                        onChange={(e) => handleChange('height_max', parseInt(e.target.value))}
                        className="w-full h-1.5 bg-gray-250 rounded-lg appearance-none cursor-pointer accent-teal-600"
                      />
                    </div>
                  </div>

                  {/* Hair / Eye Color */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-505 dark:text-gray-400 mb-1">Hair Color</label>
                      <input
                        type="text"
                        value={localFilters.hair_color || ''}
                        onChange={(e) => handleChange('hair_color', e.target.value || undefined)}
                        placeholder="E.g., Blonde"
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-505 dark:text-gray-400 mb-1">Eye Color</label>
                      <input
                        type="text"
                        value={localFilters.eye_color || ''}
                        onChange={(e) => handleChange('eye_color', e.target.value || undefined)}
                        placeholder="E.g., Blue"
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-medium"
                      />
                    </div>
                  </div>

                  {/* Agency / TFP switches */}
                  <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/25 p-3 rounded-xl border dark:border-gray-700">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Only Agency Represented</span>
                    <Switch
                      checked={!!localFilters.agency_represented}
                      onChange={(checked) => handleChange('agency_represented', checked || undefined)}
                    />
                  </div>

                  <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/25 p-3 rounded-xl border dark:border-gray-700">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Accepts TFP (Collabs)</span>
                    <Switch
                      checked={!!localFilters.accepts_tfp}
                      onChange={(checked) => handleChange('accepts_tfp', checked || undefined)}
                    />
                  </div>

                </section>
              </>
            )}

          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl flex justify-between items-center">
            <button
              onClick={handleClear}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium px-4 py-2"
            >
              Reset All
            </button>
            <Button onClick={handleApply} className="px-8">
              Show Results
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FilterModal;
